export type BucketId = 'kyc-documents' | 'farmer-photos' | 'plot-evidence' | 'generated-documents';

/**
 * What each bucket takes - a copy of storage.buckets (allowed_mime_types, file_size_limit) so a
 * wrong file is refused with a plain reason BEFORE it is hashed and sent, not after a slow upload
 * on a field connection. Storage still enforces the real limit; keep the two in step.
 */
const IMAGES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];
export const BUCKET_RULES: Record<BucketId, { types: string[]; maxBytes: number; describe: string }> = {
  'farmer-photos': { types: IMAGES, maxBytes: 8 * 1024 * 1024, describe: 'a JPG, PNG, WEBP or HEIC photo' },
  'plot-evidence': { types: [...IMAGES, 'application/pdf'], maxBytes: 8 * 1024 * 1024, describe: 'a photo (JPG, PNG, WEBP, HEIC) or a PDF' },
  'kyc-documents': { types: [...IMAGES, 'application/pdf'], maxBytes: 10 * 1024 * 1024, describe: 'a photo (JPG, PNG, WEBP, HEIC) or a PDF' },
  'generated-documents': {
    types: [...IMAGES, 'application/pdf', 'text/html', 'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    maxBytes: 20 * 1024 * 1024, describe: 'a PDF, Word document or photo',
  },
};

/** Why this bucket would refuse the file, or null. A file with no type (some HEIC pickers) is left to storage. */
export function bucketRefusal(bucket: BucketId, file: { type: string; size: number }): string | null {
  const rule = BUCKET_RULES[bucket];
  if (file.type && !rule.types.includes(file.type.toLowerCase())) {
    return `That file type (${file.type}) is not accepted here. Use ${rule.describe}.`;
  }
  if (file.size > rule.maxBytes) {
    const mb = (bytes: number) => `${(bytes / 1024 / 1024).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`;
    return `That file is ${mb(file.size)}; the limit here is ${mb(rule.maxBytes)}. Take the photo at a lower resolution or compress the PDF.`;
  }
  return null;
}
